/**
 * Shape-type classification shared by every Deckorate feature that has to
 * branch on what kind of shape it is holding.
 *
 * Extracted so the "which shapes can hold text?" rule has exactly one
 * definition. It previously lived privately inside ColorTools' colourOps.ts and
 * was copied into the action-chips selection reader; a drift between the two
 * would mean a shape the colour tools treat as text-bearing being offered the
 * wrong chip set (or vice versa).
 */

/**
 * `shape.type` normalised to lower case.
 *
 * Read through an `unknown` cast because the Office.js typings model `type` as a
 * `PowerPoint.ShapeType` enum, while the host reports a plain string whose
 * casing and vocabulary vary across API builds. Absent/undefined types
 * normalise to `""`, which matches no known kind — callers then fall through to
 * their permissive default rather than mis-classifying.
 */
export function shapeTypeOf(shape: PowerPoint.Shape): string {
  return String((shape as unknown as { type?: string }).type ?? "").toLowerCase();
}

/**
 * Shape types that cannot carry a text frame.
 *
 * Deliberately a deny-list, not an allow-list: new PowerPoint shape kinds are
 * overwhelmingly text-capable (`geometricShape`, `textBox`, placeholders, …), so
 * an unrecognised type defaults to "can hold text" instead of silently losing
 * text handling when the host adds a kind we have not seen.
 *
 * `image` and `picture` are both listed because the host reports pictures under
 * either name depending on the API build.
 */
export const NON_TEXT_SHAPE_TYPES: ReadonlySet<string> = new Set([
  "line",
  "chart",
  "media",
  "model3d",
  "ole",
  "diagram",
  "graphic",
  "ink",
  "table",
  "image",
  "picture",
]);

/** Whether this shape can hold text (and so supports the text-based tools). */
export function supportsTextFrame(shape: PowerPoint.Shape): boolean {
  return !NON_TEXT_SHAPE_TYPES.has(shapeTypeOf(shape));
}
